import {memo, useEffect, useState} from 'react';
import {
    Input,
    Popover,
    PopoverHandler,
    PopoverContent,
} from "@material-tailwind/react";
import {format} from "date-fns";
import {DayPicker} from "react-day-picker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import moment from "moment";
import {PREVENT_OUTSIDE_CLICK_ATTR} from "../hooks/useComponentVisible";

interface DatePickerProps {
    date: string,
    minDate?: string,
    onChange: (s: string) => void,
    label: string
}

const DatePicker = ({date, onChange, label, minDate}: DatePickerProps) => {
    const [localDate, setLocalDate] = useState<Date>(new Date(date));

    useEffect(() => {
        setLocalDate(new Date(date));
    }, [date]);

    const onLocalChange = (dt: Date | undefined) => {
        if (dt) {
            setLocalDate(dt);
            onChange(moment(dt).format('YYYY-MM-DD'));
        }
    }

    return (
        <div className="block md:max-w-xs md:inline-block mb-2 md:mb-auto datepicker-input">
            <Popover placement="bottom">
                <PopoverHandler>
                    <Input
                        className="dark:text-gray-500"
                        crossOrigin=''
                        label={label}
                        onChange={() => null}
                        value={localDate ? format(localDate, "yyyy-MM-dd") : ""}
                    />
                </PopoverHandler>
                <PopoverContent
                    className="bg-white dark:bg-blue-gray-900 dark:text-gray-500 dark:border-blue-gray-500 z-[100] datepicker-popover-content"
                    {...{[PREVENT_OUTSIDE_CLICK_ATTR]: true}}
                >
                    <DayPicker
                        mode="single"
                        selected={localDate}
                        onSelect={onLocalChange}
                        fromDate={minDate? new Date(minDate) : new Date(moment(date).subtract(-1, 'years').date())}
                        toYear={moment().year()}
                        className="border-0 bg-white dark:bg-blue-gray-900 dark:text-gray-500"
                        captionLayout="dropdown-buttons"
                        classNames={{
                            caption: "flex py-2 mb-4 relative items-center",
                            caption_label: "hidden",
                            nav: "flex items-center",
                            nav_button:
                                "h-6 w-6 bg-transparent hover:bg-blue-gray-50 p-1 rounded-md transition-colors duration-300",
                            nav_button_previous: "absolute left-1.5",
                            nav_button_next: "absolute right-1.5",
                            table: "w-full border-collapse",
                            head_row: "flex font-medium text-gray-900 dark:text-gray-500",
                            head_cell: "m-0.5 w-9 font-normal text-sm",
                            row: "flex w-full mt-2",
                            cell: "text-gray-600 dark:text-gray-400 rounded-md h-9 w-9 text-center text-sm p-0 m-0.5 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-gray-900/20 [&:has([aria-selected].day-outside)]:text-white [&:has([aria-selected])]:bg-gray-900/50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                            day: "h-9 w-9 p-0 font-normal",
                            day_range_end: "day-range-end",
                            day_selected:
                                "rounded-md bg-gray-900 text-white hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white",
                            day_today: "rounded-md bg-gray-200 dark:text-teal-400 dark:bg-blue-gray-800",
                            day_outside:
                                "day-outside text-gray-500 dark:text-gray-600 opacity-50 aria-selected:bg-gray-500 aria-selected:text-gray-900 aria-selected:bg-opacity-10",
                            day_disabled: "text-gray-500 dark:text-gray-700 opacity-50",
                            day_hidden: "invisible",
                            caption_dropdowns: "flex mx-8 w-full grid grid-cols-2 justify-center relative items-center",
                            vhidden: "hidden",
                            month: "text-sm font-medium text-gray-900 dark:text-white bg-transparent",
                            dropdown: "w-full outline-none text-sm font-medium text-gray-900 dark:text-white bg-transparent"
                        }}
                        components={{
                            IconLeft: ({...props}) => (
                                <FontAwesomeIcon {...props} icon={solid('chevron-left')} className="h-4 w-4 stroke-2"/>
                            ),
                            IconRight: ({...props}) => (
                                <FontAwesomeIcon {...props} icon={solid('chevron-right')} className="h-4 w-4 stroke-2"/>
                            ),
                        }}
                        showOutsideDays
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}

export default memo(DatePicker);
